import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Save, HelpCircle, CheckCircle2, Circle } from "lucide-react";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/**
 * iter-170 — Talent Passport section for answering the knockout questions
 * employers are asking across currently-published roles. One answer per
 * unique question (deduped server-side by normalized text) applies to
 * every role asking the same thing — so a talent moves out of the
 * "Didn't meet filters" collapsed bucket as soon as they answer here.
 */
export const KnockoutAnswersSection = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({});    // qid -> answer
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/professional/knockout-questions");
            setRows(data.questions || []);
            const d = {};
            for (const q of (data.questions || [])) {
                if (q.current_answer !== null && q.current_answer !== undefined) {
                    d[q.id] = q.current_answer;
                }
            }
            setDraft(d);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const set = (qid, value) => setDraft((d) => ({ ...d, [qid]: value }));

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.post("/professional/knockout-answers",
                                            { answers: draft });
            toast.success(`Saved ${data.saved} answer${data.saved === 1 ? "" : "s"}.`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    if (loading) {
        return <div className="py-6 flex justify-center"><Loader2 size={16} className="animate-spin text-emerald-500" /></div>;
    }
    if (rows.length === 0) {
        return (
            <div className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground text-center" data-testid="knockouts-empty">
                No open roles are asking screening questions right now. Check back later — new postings surface here automatically.
            </div>
        );
    }

    return (
        <div data-testid="knockouts-section" className="rounded-xl border border-border bg-card p-5 sm:p-6">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                <HelpCircle size={14} /> <p className="overline">Screening answers</p>
            </div>
            <h2 className="mt-1 font-heading font-semibold text-lg text-foreground">Pre-answer common screening questions</h2>
            <p className="mt-1 text-sm text-muted-foreground">
                Answer once — your reply applies to every role asking the same question. Blank answers keep you in &quot;Didn&apos;t meet filters&quot; for those roles until you reply.
            </p>

            <ul className="mt-4 space-y-3" data-testid="knockouts-list">
                {rows.map((q) => (
                    <li key={q.id} data-testid={`knockout-row-${q.id}`} className="rounded-lg border border-border bg-background p-3">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-foreground">{q.question}</div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    Asked by {q.roles_asking} role{q.roles_asking === 1 ? "" : "s"}
                                    {draft[q.id] !== undefined && draft[q.id] !== null && draft[q.id] !== "" && (
                                        <span className="ml-2 inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400">
                                            <CheckCircle2 size={10} /> Answered
                                        </span>
                                    )}
                                </div>
                            </div>
                            <AnswerControl q={q} value={draft[q.id]} onChange={(v) => set(q.id, v)} />
                        </div>
                    </li>
                ))}
            </ul>

            <div className="mt-4 flex justify-end">
                <button
                    onClick={save} disabled={saving}
                    data-testid="knockouts-save"
                    className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                >
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    Save answers
                </button>
            </div>
        </div>
    );
};

const AnswerControl = ({ q, value, onChange }) => {
    if (q.answer_type === "yes_no") {
        return (
            <div className="inline-flex items-center gap-1" data-testid={`knockout-yesno-${q.id}`}>
                {[
                    { v: true, label: "Yes" },
                    { v: false, label: "No" },
                ].map(({ v, label }) => {
                    const selected = value === v;
                    return (
                        <button key={String(v)} type="button"
                                onClick={() => onChange(v)}
                                data-testid={`knockout-${q.id}-${label.toLowerCase()}`}
                                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold border transition ${
                                    selected
                                        ? (v
                                            ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
                                            : "bg-rose-500/15 border-rose-500/40 text-rose-700 dark:text-rose-400")
                                        : "bg-card border-border text-foreground hover:bg-muted/40"
                                }`}>
                            {selected ? <CheckCircle2 size={11} /> : <Circle size={11} />}
                            {label}
                        </button>
                    );
                })}
            </div>
        );
    }
    return (
        <Input
            value={value || ""} onChange={(e) => onChange(e.target.value)}
            placeholder="Short answer…"
            data-testid={`knockout-text-${q.id}`}
            maxLength={200}
            className="h-8 text-sm w-56"
        />
    );
};

export default KnockoutAnswersSection;
